import {CloudOff, Database, type LucideIcon} from 'lucide-react';
import {Tooltip, TooltipContent, TooltipTrigger} from '@common-web/components/ui/tooltip';
import {cn} from '@common-web/lib/utils';
import type {VbaStatus} from '@common/groupPipeline/types';

export const VBA_STATUS_DISPLAY: Record<
  VbaStatus,
  {label: string; icon: LucideIcon; color: string; chip: string; hint: string | null}
> = {
  NOT_IN_VBA: {
    label: 'Not in VBA',
    icon: CloudOff,
    color: 'text-yellow-700',
    // Warm and outlined while waiting on the sync; never blue, so it can't read as a phase.
    chip: 'bg-yellow-50 text-yellow-700 ring-1 ring-inset ring-yellow-700/35',
    hint: 'Pending VBA sync. Some details may be missing until then.',
  },
  IN_VBA: {
    label: 'In VBA',
    icon: Database,
    color: 'text-slate-700',
    chip: 'bg-slate-100 text-slate-700',
    hint: null,
  },
};

// A chip with icon and word, explained on hover when there's more to say. `quiet` hides the usual case (in
// VBA) so lists only flag groups still waiting on the sync.
export function VbaStatusIndicator({status, quiet}: {status: VbaStatus; quiet?: boolean}) {
  if (quiet && status === 'IN_VBA') return null;
  const {label, icon: Icon, chip, hint} = VBA_STATUS_DISPLAY[status];
  const content = (
    <>
      <Icon className='h-3.5 w-3.5 shrink-0' />
      {label}
    </>
  );
  const className = cn(
    'inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md px-2 py-0.5 text-xs font-semibold',
    chip,
  );
  if (!hint) return <span className={className}>{content}</span>;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          tabIndex={0}
          aria-label={`${label}. ${hint}`}
          className={cn(className, 'cursor-help')}
        >
          {content}
        </span>
      </TooltipTrigger>
      <TooltipContent className='max-w-xs bg-gray-900 text-white'>{hint}</TooltipContent>
    </Tooltip>
  );
}
